// Markdown pipe tables inside an activity TEXT item.
//
// A table is stored as plain markdown in ONE text item (question body or solution):
//
//   | Yaş Aralığı | Kadın | Erkek |
//   | :--- | :---: | :---: |
//   | 18-40 yaş | 4 | 9 |
//
// and every renderer draws it as a real table. This module only PARSES: it splits a text into
// text and table segments, leaving each cell as raw text so the caller renders **bold**,
// __underline__ and $$math$$ inside it exactly as it does anywhere else.
//
// Keep in step with the twins in catchup-admin-web (same path), catchup-mobile
// (src/utils/markdownTable.ts) and the backend's MarkdownTableNormalizer: the four must agree on
// what is a table, or a row renders as a table on one surface and as raw "| a | b |" on another.

import { normalizeLatexDelimiters } from "./CatchtivityUtilization";

export type MarkdownTableAlign = "left" | "center" | "right";

export interface IMarkdownTable {
  header: string[];
  align: MarkdownTableAlign[];
  rows: string[][];
}

export type MarkdownTextSegment =
  | { kind: "text"; text: string }
  | { kind: "table"; table: IMarkdownTable };

// $$…$$ or `…`: a pipe inside math (|AB|, P(A|B)) is part of the formula, not a cell
// boundary. Single-$ math is first rewritten to $$…$$ by the same normalizer the text renderer
// uses (normalizeLatexDelimiters), so "what is a formula" means the same thing in both places —
// including its currency rule ("$2 | $3" stays text).
// An escaped pipe (\\|) is a literal "|" inside a cell, as in GFM.
const MATH_SPAN = /\$\$[\s\S]*?\$\$|`[^`]*`|\\\|/g;
const SEPARATOR_CELL = /^\s*:?-+:?\s*$/;
const MASK = /\uE000(\d+)\uE000/g;

const maskMath = (text: string, masks: string[]): string =>
  text.replace(MATH_SPAN, (match) => {
    masks.push(match);
    return `\uE000${masks.length - 1}\uE000`;
  });

// An escape mask comes back as a plain "|"; a math/code span comes back exactly as written
// (inside $$…$$, \\| is LaTeX's double bar ‖ and must survive).
const unmaskMath = (text: string, masks: string[]): string =>
  text.replace(MASK, (_m, index) => (masks[Number(index)] === "\\|" ? "|" : masks[Number(index)]));

/** The cells of one table line, or null when the line is not a table row. */
export const splitTableRow = (line: string): string[] | null => {
  // An em space (a model's indent, or between words) reads as a normal space.
  let l = line.replace(/\u2003/g, " ").trim();
  // A generator used to end every row with a sentence period ("| a | b |.").
  if (l.endsWith("|.")) l = l.slice(0, -1);
  if (!l) return null;
  const masks: string[] = [];
  let masked = maskMath(l, masks);
  if (!masked.includes("|")) return null;
  if (masked.startsWith("|")) masked = masked.slice(1);
  if (masked.endsWith("|")) masked = masked.slice(0, -1);
  const cells = masked
    .split("|")
    .map((cell) => unmaskMath(cell, masks).trim());
  return cells.length >= 2 ? cells : null;
};

const isSeparatorRow = (line: string): boolean => {
  const cells = splitTableRow(line);
  return !!cells && cells.every((cell) => SEPARATOR_CELL.test(cell));
};

const alignmentOf = (cell: string, columnIndex: number): MarkdownTableAlign => {
  const c = cell.trim();
  if (c.startsWith(":") && c.endsWith(":")) return "center";
  if (c.endsWith(":")) return "right";
  if (c.startsWith(":")) return "left";
  return columnIndex === 0 ? "left" : "center";
};

/**
 * Splits `text` into text and table segments. A table is a header row, a separator row
 * (`|---|:---:|`) and any number of rows after it; rows are padded to the widest row. Text
 * without a table comes back as one text segment, unchanged.
 */
export const splitMarkdownTables = (text: string): MarkdownTextSegment[] => {
  if (!text || !text.includes("|") || !text.includes("-")) {
    return [{ kind: "text", text: text ?? "" }];
  }
  // Tables are FOUND on the normalized text (single-$ math → $$…$$), but text outside a table is
  // handed back exactly as written, so a caller that measures or prints it sees no change. The
  // normalizer never adds or removes a newline, so the two line lists stay index-aligned.
  const rawLines = text.split("\n");
  const normalizedLines = normalizeLatexDelimiters(text).split("\n");
  const lines = normalizedLines.length === rawLines.length ? normalizedLines : rawLines;
  const segments: MarkdownTextSegment[] = [];
  let buffer: string[] = [];
  const flush = () => {
    if (buffer.length > 0) {
      segments.push({ kind: "text", text: buffer.join("\n") });
      buffer = [];
    }
  };
  let i = 0;
  while (i < lines.length) {
    const header = splitTableRow(lines[i]);
    if (header && !isSeparatorRow(lines[i]) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      const separator = splitTableRow(lines[i + 1]) as string[];
      const rows: string[][] = [];
      // A table written with outer pipes ends at the first line without a leading one, so a stem
      // line right under it ("Buna göre |x| kaçtır?") is not swallowed as an extra row.
      const outerPipes = lines[i].replace(/\u2003/g, "").trim().startsWith("|");
      let j = i + 2;
      while (j < lines.length) {
        if (outerPipes && !lines[j].replace(/\u2003/g, "").trim().startsWith("|")) break;
        const row = splitTableRow(lines[j]);
        if (!row || isSeparatorRow(lines[j])) break;
        // A row followed by a separator is the header of the NEXT table (two tables back to back).
        if (j + 1 < lines.length && isSeparatorRow(lines[j + 1])) break;
        rows.push(row);
        j++;
      }
      const columns = Math.max(header.length, ...rows.map((r) => r.length));
      const pad = (cells: string[]) => [...cells, ...Array(columns - cells.length).fill("")];
      flush();
      segments.push({
        kind: "table",
        table: {
          header: pad(header),
          align: Array.from({ length: columns }, (_, c) => alignmentOf(separator[c] ?? "---", c)),
          rows: rows.map(pad),
        },
      });
      i = j;
    } else {
      buffer.push(rawLines[i] ?? lines[i]);
      i++;
    }
  }
  flush();
  // Drop the blank-line-only text segments a table leaves around itself.
  return segments.filter((s) => s.kind === "table" || s.text.trim() !== "" || segments.length === 1);
};

export const containsMarkdownTable = (text: string): boolean =>
  splitMarkdownTables(text).some((segment) => segment.kind === "table");
